package one.healthium.onboarding.ui.screens

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import one.healthium.onboarding.domain.configs.AgeInputScreenConfig
import one.healthium.onboarding.ui.UserDataProvider
import one.healthium.onboarding.ui.UserProfileUiModel
import one.healthium.onboarding.ui.bindPlaceholderValues
import one.healthium.ui.components.CtaButton
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppText
import one.healthium.ui.compose.components.AppTextField
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_3

interface AgeInputEventDelegate {
    fun onNextClicked()
}

@Composable
internal fun AgeInputScreen(
    eventDelegate: AgeInputEventDelegate,
    screenConfig: AgeInputScreenConfig,
) {
    UserDataProvider { userState, contract ->
        AppScaffold {
            AgeInputScreenContent(
                screenConfig = screenConfig,
                userState = userState,
                onNextClicked = eventDelegate::onNextClicked,
                onAgeChanged = contract::onAgeChanged,
            )
        }
    }
}

@Composable
internal fun AgeInputScreenContent(
    modifier: Modifier = Modifier,
    screenConfig: AgeInputScreenConfig,
    userState: UserProfileUiModel,
    onNextClicked: () -> Unit,
    onAgeChanged: (Int?) -> Unit,
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .statusBarsPadding()
            .navigationBarsPadding()
            .padding(horizontal = 24.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        AppText(
            text = screenConfig.title?.bindPlaceholderValues(userState),
            style = AppTheme.textStyles.header,
            color = AppTheme.colors.text100,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(24.dp))
        Spacer(Modifier.weight(0.7f))
        AppTextField(
            modifier = Modifier.fillMaxWidth(),
            value = userState.age?.toString().orEmpty(),
            onValueChange = { onAgeChanged(it.toIntOrNull()) },
            textStyle = AppTheme.textStyles.textField.copy(textAlign = TextAlign.Center),
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.NumberPassword,
                imeAction = ImeAction.Done,
            ),
            showKeyboardAutomaticallyIfEmpty = true,
            maxLines = 1,
        )
        Spacer(Modifier.height(24.dp))
        val validationError = userState.age.getError()
        Box(Modifier.weight(1f)) {
            AppText(
                text = validationError ?: screenConfig.defaultHint,
                style = AppTheme.textStyles.caption,
                color = gray_3,
                textAlign = TextAlign.Center,
            )
        }
        Spacer(Modifier.height(8.dp))
        CtaButton(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 14.dp),
            text = screenConfig.ctaTitle,
            enabled = validationError == null && userState.age.isValid(),
            onClick = onNextClicked,
        )
        Spacer(Modifier.height(24.dp))
        Spacer(Modifier.navigationBarsPadding().imePadding())
    }
}

@Composable
private fun Int?.getError(): String? {
    if (this == null || this == 0) return null

    return if (isValid()) null else "Age should be more than 18"
}

private fun Int?.isValid(): Boolean {
    if (this == null) return false

    if (this < 18) return false
    return true
}
